import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Button,
  Dropdown,
  Menu,
  Skeleton,
  Spin,
  message,
  Grid,
  Steps,
  Tabs,
  Alert,
} from "antd";
import { MoreOutlined } from "@ant-design/icons";

// Redux & Store
import { RootState, useAppDispatch, useAppSelector } from "../../../../store";

// API & Types
import {
  deleteQuotationAPI,
  duplicateQuotationAPI,
  createRevisionQuotationAPI,
  generateQuotationPdfAPI,
  getQuotationDetailsAPI,
  notifyClientAPI,
  reopenQuotationAPI,
  updateQuotatioStatusAPI,
  toggleArchiveQuoteAPI,
  previewQuoteEmailAPI,
  openSystemMailer,
  requestCustomerApprovalAPI,
} from "../quotationApis";
import {
  findStepIndexInPlaylist,
  isStepDisabledInPlaylist,
} from "../quotationSlice";
import {
  QuoteStatus,
  QuoteStatusLabels,
  CreateQuotationStepsEnum,
  QUOTATION_SCROLL_KEY,
  QUOTATION_FILTER_KEY,
  QUOTATION_REFETCH_KEY,
  STATIC_GROUP_CHILDREN,
  getChildrenForCategory,
} from "../quotationTypes";
import EstimationFloorDetails from "../../Estimations/steps/FloorDetails";
import { calculateStepPlaylist, setQuotationDetails } from "../quotationSlice";
import {
  CategoriesSlugWithSummaries,
  CategorySlugs,
} from "../Components/Products/SummaryHelper";
import placeHolderImage from "../../../../Assets/Images/placeholderImage.png";

// Assets & Helpers
import { CommonConfirmationModal } from "../../../../Components/CommonConfirmationModal";
import { DownloadMailerAppModal } from "../../../../Components/DownloadMailerAppModal";
import {
  DesktopUpdateModal,
  isVersionNewer,
} from "../../../../Components/DesktopUpdateModal";
import { useSocket } from "../../../../contexts/SocketContext";
import axiosInstance from "../../../../helpers/axios";
import { svg } from "../../../../Assets/svg";
import CustomerDetails from "./CustomerDetails";
import ProductDetails from "./ProductDetails";
import ServiceDetails from "./ServiceDetails";
import DesignsDetails from "./DesignDetails";
import BespokeItemsDetails from "./BespokeItemsDetails";
// import ChecklistDetails from "./ChecklistDetails";
import NotesDetails from "./NotesDetails";
import AddApprovalOverrideNoteModal from "../Components/AddApprovalOverrideNoteModal";
import ViewOverrideNoteModal from "../Components/ViewOverrideNoteModal";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../../helpers/permissionModule";
import NoPermissionPage from "../../../Components/NoPermissionPage";
import FullScreenLoader from "../../../Components/FullScreenLoader";

import { createOrderAPI } from "../../Orders/orderApis";
import {
  alias,
  getFullAwsUrl,
  INVOICE_WARNING_VISIBLE_ROLES,
  ROLE,
  isInvoiceFeatureEnabled,
} from "../../../../helpers/utils";
import {
  getLoggedInUserId,
  isCustomerLoggedIn,
  getDecryptedUserData,
  isAdminSubAdminLoggedIn,
  getUserRole,
} from "../../../../helpers/bycrypt";
import { RoleTypesEnum } from "../../RolesAndPermissions/rolesAndPermissionsTypes";
import { StorageGroup } from "../CreateQuotations";
import DeliveryDetails from "./DeliveryDetails";
import LoadNoticeDetails from "./LoadNoticeDetails";
import BOMDetails from "../Components/BOMDetails";
import { ModifyDeliveryAddressModal } from "../../ClientsDistributors/Components/ModifyDeliveryAddressModal";
import CreateOrderConfirmationModal from "../Components/CreateOrderConfirmationModal";

// Components
import Review from "../Components/Review";
import ConfigureDrawingsModal from "../Components/ConfigureDrawingsModal";
import { Capacitor } from "@capacitor/core";
import { getCapacitorBaseUrl } from "../../../../helpers/common";
import { openQuoteEmailWithPdf } from "../../../../helpers/emailCapacitor";
import { PROJECT_REFETCH_KEY } from "../../Projects/Projects";
import CustomerOnHoldWarningModal from "../../Orders/Components/Modals/CustomerOnHoldWarningModal";

const { useBreakpoint } = Grid;

export const statusFindClass = (status: string) => {
  switch (status) {
    case QuoteStatus.DRAFT:
      return "statusDraft";
    case QuoteStatus.SAVED:
      return "statusSave";
    case QuoteStatus.SENT:
      return "statusSent";
    case QuoteStatus.DESIGN_PENDING:
      return "statusDesignPending";
    case QuoteStatus.APPROVED:
    case QuoteStatus.APPROVED_OVERRIDE:
      return "statusApproved";
    case QuoteStatus.DESIGN_APPROVED:
      return "statusDesignApproved";
    case QuoteStatus.EXPIRED:
      // case QuoteStatus.REJECTED:
      return "statusExpired";
    case QuoteStatus.ORDER_CREATED:
      return "statusOrderCreated";
    case QuoteStatus.ADMIN_APPROVED:
      return "statusAdminApproved";
    case QuoteStatus.PENDING_ADMIN_APPROVAL:
      return "statusPendingAdminApproval";
    default:
      return "statusDraft"; // Fallback
  }
};

export const countAnsweredQuestions = (checklistAnswers: any[]) => {
  if (!checklistAnswers || !Array.isArray(checklistAnswers)) return 0;

  return checklistAnswers.filter((item) => {
    const { ans_type, answer } = item;

    // 1. Basic Null Check
    if (answer === null || answer === undefined || answer === "") {
      return false;
    }

    // 2. Logic by Type
    switch (ans_type) {
      case "YES_NO":
        // Rule: Only check presence of boolean (true or false)
        return answer === true || answer === false;

      case "FILE_UPLOAD":
        // Rule: Check for at least one file
        return Array.isArray(answer) && answer.length > 0;

      default:
        // Rule: Simple Text / Long Text (already checked for null/empty above)
        return true;
    }
  }).length;
};

// --- MAIN COMPONENT ---
const QuotationDetails = () => {
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.QUOTATIONS,
  );
  const userData = getDecryptedUserData();
  const { authLoading } = useAppSelector((state: RootState) => state.loading);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { backPath = "", backTabPath = "" } = location.state || {};
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Responsive Logic (Same as CreateQuotations)
  const screens = useBreakpoint();
  const position = screens.lg ? "left" : "top";

  // State
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any | null>(null);

  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const isCreatedForSelf = getLoggedInUserId() === data?.customer_id?._id;

  const isClientViewingOwnQuote = isCustomerLoggedIn() && isCreatedForSelf;
  const isClientViewingOtherUserQuote =
    isCustomerLoggedIn() && !isCreatedForSelf;

  const [failedAddressData, setFailedAddressData] = useState<any>(null);
  const [drawingRequiredData, setDrawingRequiredData] = useState<any>(null);
  const pendingOrderPayloadRef = useRef<any>(null);

  // Active Tab State (Synced with URL)
  const activeStep =
    (searchParams.get("tab") as string) ||
    CreateQuotationStepsEnum.CUSTOMER_DETAILS;

  // Modal State
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [isViewNoteModalOpen, setIsViewNoteModalOpen] = useState(false);
  const [isAdminApprovalModalOpen, setIsAdminApprovalModalOpen] =
    useState(false);
  const [
    isCustomerApprovalRequiredModalOpen,
    setIsCustomerApprovalRequiredModalOpen,
  ] = useState(false);
  const [isDownloadAndMarkSentModalOpen, setIsDownloadAndMarkSentModalOpen] =
    useState(false);
  const [isDownloadingAndMarkingSent, setIsDownloadingAndMarkingSent] =
    useState(false);
  const [approving, setApproving] = useState(false);

  const [idToDuplicate, setIdToDuplicate] = useState<string | null>(null);
  const [idToCreateRevision, setIdToCreateRevision] = useState<string | null>(
    null,
  );
  const [isCreatingRevision, setIsCreatingRevision] = useState<boolean>(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [requestingApproval, setRequestingApproval] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);

  const [customerOnHoldModal, setCustomerOnHoldModal] = useState(false);
  const [customerOnHoldMessage, setCustomerOnHoldMessage] = useState("");

  const quotationCustomerId =
    data?.customer_id?._id ||
    data?.customer_id ||
    data?.customer_snapshot?._id ||
    data?.customer_snapshot?.id;

  const getRemainingCooldown = (cId: string | undefined): number => {
    if (!cId) return 0;
    const savedTimestamp = localStorage.getItem(`customer_reminder_${cId}`);
    if (!savedTimestamp) return 0;
    const elapsedSeconds = Math.floor(
      (Date.now() - Number(savedTimestamp)) / 1000,
    );
    const remaining = 30 * 60 - elapsedSeconds;
    return remaining > 0 ? remaining : 0;
  };

  useEffect(() => {
    if (!quotationCustomerId) return;
    const remaining = getRemainingCooldown(quotationCustomerId);
    setCooldownSeconds(remaining);

    if (remaining <= 0) return;

    const interval = setInterval(() => {
      const updated = getRemainingCooldown(quotationCustomerId);
      setCooldownSeconds(updated);
      if (updated <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [quotationCustomerId, requestingApproval]);

  const formatCooldownTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0",
    )}`;
  };

  const handleRequestApproval = () => {
    if (!quotationCustomerId) {
      message.error("Customer ID not found");
      return;
    }

    setRequestingApproval(true);
    dispatch(
      requestCustomerApprovalAPI({
        customerId: quotationCustomerId,
        onSuccess: () => {
          setRequestingApproval(false);
          localStorage.setItem(
            `customer_reminder_${quotationCustomerId}`,
            String(Date.now()),
          );
          setCooldownSeconds(30 * 60);
        },
        onError: () => {
          setRequestingApproval(false);
        },
      }),
    );
  };

  const { socket, isConnected }: any = useSocket();
  const [showDesktopAppError, setShowDesktopAppError] = useState(false);
  const [showDesktopUpdateModal, setShowDesktopUpdateModal] =
    useState<boolean>(false);
  const [desktopAppCurrentVersion, setDesktopAppCurrentVersion] =
    useState<string>("1.0.0");
  const pendingSendMailRef = useRef<(() => void) | null>(null);
  const [isContantLoading, setIsContantLoading] = useState<boolean>(true);
  const mailerTimeoutRef = useRef<React.MutableRefObject<any> | any>(null);

  useEffect(() => {
    if (!socket) return;

    const handleMailerLoader = (data: any) => {
      console.log(
        "📥 Received mailer:trigger-loader event in QuotationDetails:",
        data,
      );
      if (mailerTimeoutRef.current) {
        clearTimeout(mailerTimeoutRef.current);
        mailerTimeoutRef.current = null;
      }
      if (
        data &&
        (data.loader === false ||
          data.loading === false ||
          data.loder === false ||
          data.status === "success" ||
          data.status === "error")
      ) {
        setIsContantLoading(false);
      } else if (
        data &&
        (data.loader === true ||
          data.loading === true ||
          data.loder === true ||
          data.status === "processing")
      ) {
        setIsContantLoading(true);
      }
    };

    socket.on("mailer:trigger-loader", handleMailerLoader);

    return () => {
      socket.off("mailer:trigger-loader", handleMailerLoader);
      if (mailerTimeoutRef.current) {
        clearTimeout(mailerTimeoutRef.current);
      }
    };
  }, [socket]);

  const triggerEmailWithDesktopAppCheck = (sendEmailCallback: () => void) => {
    if (!socket || !isConnected) {
      console.error("Global socket is not connected!");
      message.destroy("opening_mailer_key");
      setShowDesktopAppError(true);
      return;
    }

    const targetRoom = localStorage.getItem("sm_target_room");
    const requiredVersion =
      process.env.REACT_APP_DESKTOP_APP_VERSION || "1.0.1";

    let responseReceived = false;
    let checkVersionTimeout: NodeJS.Timeout | null = null;

    const handleCheckVersionResponse = (data: any) => {
      if (responseReceived) return;
      responseReceived = true;

      if (checkVersionTimeout) {
        clearTimeout(checkVersionTimeout);
        checkVersionTimeout = null;
      }
      if (socket) {
        socket.off("check_version_response", handleCheckVersionResponse);
      }

      console.log(
        "📥 Received check_version_response payload in QuotationDetails:",
        data,
      );
      const currentVersion =
        data?.current_version || data?.app_version || data?.version || "1.0.0";
      setDesktopAppCurrentVersion(currentVersion);

      const isOutdated = isVersionNewer(requiredVersion, currentVersion);
      const isMatched =
        !isOutdated &&
        (data?.is_matched !== undefined ? Boolean(data.is_matched) : true);

      if (!isMatched) {
        console.warn(
          `⚠️ Desktop app version mismatch. Required: ${requiredVersion}, Installed: ${currentVersion}`,
        );
        message.destroy("opening_mailer_key");
        pendingSendMailRef.current = () => {
          sendEmailCallback();
        };
        setShowDesktopUpdateModal(true);
      } else {
        console.log("✅ Desktop app is running and version matches!");
        sendEmailCallback();
      }
    };

    checkVersionTimeout = setTimeout(() => {
      if (!responseReceived) {
        responseReceived = true;
        if (socket) {
          socket.off("check_version_response", handleCheckVersionResponse);
        }
        console.warn(
          "⚠️ Desktop Mailer App did not respond to check_version within timeout.",
        );
        message.destroy("opening_mailer_key");

        if (targetRoom) {
          console.warn(
            "⚠️ Legacy desktop app detected in QuotationDetails. Prompting update modal.",
          );
          setDesktopAppCurrentVersion("1.0.0 (Legacy)");
          pendingSendMailRef.current = () => {
            sendEmailCallback();
          };
          setShowDesktopUpdateModal(true);
        } else {
          setShowDesktopAppError(true);
        }
      }
    }, 2500);

    socket.on("check_version_response", handleCheckVersionResponse);

    console.log(
      "📤 Emitting check_version to socket for target room:",
      targetRoom || "all",
    );
    socket.emit("check_version", {
      target_room: targetRoom || undefined,
      required_version: requiredVersion,
      version: requiredVersion,
    });
  };

  const [isNotifyClientModalOpen, setIsNotifyClientModalOpen] = useState(false);
  const [notifyingClient, setNotifyingClient] = useState(false);
  const [pendingSentModalOpen, setPendingSentModalOpen] = useState(false);
  const [pendingSentCBs, setPendingSentCBs] = useState<{
    onSuccessCB?: () => void;
    onErrorCB?: () => void;
  } | null>(null);

  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [isReopenLoading, setIsReopenLoading] = useState(false);

  const [exporting, setExporting] = useState(false);

  const [submittingCreateOrder, setSubmittingCreateOrder] = useState(false);
  const [isCreateOrder, setIsCreateOrder] = useState(false);
  const messageCloserRef = useRef<any>(null);

  const [archiveActionData, setArchiveActionData] = useState<{
    id: string;
    type: "archive" | "unarchive";
  } | null>(null);
  const [isTogglingArchive, setIsTogglingArchive] = useState(false);

  useEffect(() => {
    return () => {
      if (messageCloserRef.current) {
        messageCloserRef.current();
      }
    };
  }, []);

  const checklistCount = useMemo(() => {
    return countAnsweredQuestions(data?.checklist_answers);
  }, [data]);

  const totalQuestions = data?.checklist_answers?.length || 0;

  // --- Fetch Data ---
  useEffect(() => {
    if (data?.is_archived) {
      if (
        userData?.role?.key !== ROLE.ADMIN &&
        userData?.role?.key !== ROLE.SUB_ADMIN
      ) {
        navigate("/quotations");
      }
    }
  }, [data?.is_archived, userData?.role?.key, navigate]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    dispatch(
      getQuotationDetailsAPI({
        quote_id: id,
        onSuccess: (response: any) => {
          setData(response);
          dispatch(setQuotationDetails(response));
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
        onNotFound: () => {
          message.error("This quotaions is no longer available.");
          navigate("/quotations");
        },
      }),
    );
  }, [id, dispatch]);

  const STATIC_STORAGE_GROUPS = useMemo((): StorageGroup[] => {
    return (data?.categories_snapshot || []).map((item: any) => ({
      ...item,
      label: item.name,
      key: item._id,
      slug: item.slug,
      category_id: item.category_id,
      item_id: item._id, // the snapshot _id
      product_type_name: item.product_type_name,
    }));
  }, [data]);

  useEffect(() => {
    if (STATIC_STORAGE_GROUPS.length > 0) {
      setExpandedGroups(STATIC_STORAGE_GROUPS.map((group) => group.key));
    }
  }, [STATIC_STORAGE_GROUPS]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) =>
      prev.includes(key) ? prev.filter((g) => g !== key) : [...prev, key],
    );
  };

  const getStaticNestedStepKey = (groupKey: string, childKey: string) =>
    `${groupKey}__${childKey}`;

  // --- Handlers ---
  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
    setIsMobileNavOpen(false);
  };

  const fullStepPlaylist = useMemo(() => {
    return calculateStepPlaylist(
      data?.categories_snapshot || [],
      data?.installation_mark_up_required || false,
    );
  }, [data?.categories_snapshot, data?.installation_mark_up_required]);

  const handleModify = () => {
    sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
    sessionStorage.removeItem(QUOTATION_FILTER_KEY);
    sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");

    const lastCompletedStep =
      data?.last_completed_step || CreateQuotationStepsEnum.CUSTOMER_DETAILS;

    let targetStep = activeStep;
    // Redirect to last completed step if the active step is disabled
    if (
      isStepDisabledInPlaylist(
        fullStepPlaylist,
        activeStep,
        lastCompletedStep,
        data,
      )
    ) {
      targetStep = lastCompletedStep;
    }

    navigate(
      `/quotations/create-quotation?step=${targetStep}&quotationId=${data._id}&mode=edit`,
    );
  };

  const handleNavigateBack = () => {
    if (backPath && backTabPath && backTabPath !== "false") {
      navigate(`${backPath}`, {
        state: { tabPath: backTabPath },
      });
    } else if (backPath) {
      navigate(backPath);
    } else {
      navigate("/quotations");
    }
  };

  const handleDeleteConfirm = () => {
    if (!id) return;
    setDeleting(true);
    dispatch(
      deleteQuotationAPI({
        id: id,
        onSuccess: () => {
          sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
          sessionStorage.removeItem(QUOTATION_FILTER_KEY);
          sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");
          sessionStorage.setItem(PROJECT_REFETCH_KEY, "1");
          setDeleting(false);
          setShowDeleteModal(false);
          handleNavigateBack();
        },
        onError: () => setDeleting(false),
      }),
    );
  };

  const handleArchiveConfirm = () => {
    if (!archiveActionData) return;
    setIsTogglingArchive(true);
    dispatch(
      toggleArchiveQuoteAPI({
        quote_id: archiveActionData.id,
        is_archived: archiveActionData.type === "archive",
        onSuccess: () => {
          sessionStorage.setItem(PROJECT_REFETCH_KEY, "1");
          setIsTogglingArchive(false);
          setArchiveActionData(null);
          handleNavigateBack();
        },
        onError: () => {
          setIsTogglingArchive(false);
        },
      }),
    );
  };

  const executeStatusUpdate = (
    status:
      | "saved"
      | "sent"
      | "design_pending"
      | "design_approved"
      | "admin_approved"
      | "pending_admin_approval",
    onSuccessCB?: () => void,
    onErrorCB?: () => void,
  ) => {
    const payload = {
      quote_id: id as string,
      status,
    };
    dispatch(
      updateQuotatioStatusAPI({
        payload,
        onSuccess: (responseData) => {
          setData(responseData);
          if (onSuccessCB) onSuccessCB();
        },
        onError: () => {
          if (onErrorCB) onErrorCB();
        },
        onNoItemsError: () => {
          if (onErrorCB) onErrorCB();
        },
      }),
    );
  };

  const changeStatusHandler = (
    status:
      | "saved"
      | "sent"
      | "design_pending"
      | "design_approved"
      | "admin_approved"
      | "pending_admin_approval",
    onSuccessCB?: () => void,
    onErrorCB?: () => void,
  ) => {
    if (status === "sent") {
      setNotifyingClient(true);
      message.loading({
        content: "Preparing email format and opening mail app...",
        key: "opening_mailer_key",
        duration: 0,
      });
      dispatch(
        previewQuoteEmailAPI({
          payload: { quote_id: id as string },
          onSuccess: async (emailData) => {
            setNotifyingClient(false);
            message.destroy("opening_mailer_key");
            try {
              if (Capacitor.isNativePlatform()) {
                message.loading({
                  content: "Preparing quotation PDF and opening mail app...",
                  key: "opening_mailer_key",
                  duration: 0,
                });
                try {
                  await openQuoteEmailWithPdf(id as string, emailData);
                  message.destroy("opening_mailer_key");
                  setIsContantLoading(false);
                  setPendingSentCBs({ onSuccessCB, onErrorCB });
                  setPendingSentModalOpen(true);
                } catch (error) {
                  console.error("Native email composer error:", error);
                  message.destroy("opening_mailer_key");
                  message.error(
                    "Failed to prepare the quotation email. Please try again.",
                  );
                }
                return;
              }
              triggerEmailWithDesktopAppCheck(() => {
                const targetRoom = localStorage.getItem("sm_target_room");
                console.log(
                  "Emitting email data to global socket for target room:",
                  targetRoom || "all",
                );
                socket.emit("mailer:trigger-email", {
                  target_room: targetRoom || undefined,
                  to: emailData.to || "",
                  cc: emailData.cc || "",
                  subject: emailData.mail_subject || "",
                  body: emailData.html_template || "",
                  pdf_url: emailData.pdf_url || "",
                  pdf_download_url: `${process.env.REACT_APP_API_URL}/quotes/generate-pdf?quote_id=${id}&download=true`,
                  pdf_name: emailData.pdf_name || "Quotation.pdf",
                });

                setPendingSentCBs({ onSuccessCB, onErrorCB });
                setIsContantLoading(true);
                setPendingSentModalOpen(true);

                if (mailerTimeoutRef.current)
                  clearTimeout(mailerTimeoutRef.current);
                mailerTimeoutRef.current = setTimeout(() => {
                  console.warn(
                    "⚠️ No response from Electron Mailer App within timeout.",
                  );
                  setIsContantLoading(false);
                  setPendingSentModalOpen(false);
                  setShowDesktopAppError(true);
                }, 4000);
              });
            } catch (error) {
              console.error(
                "Could not connect to Desktop App. Is it running?",
                error,
              );
              message.destroy("opening_mailer_key");
              setShowDesktopAppError(true);
            }
          },
          onError: () => {
            setNotifyingClient(false);
            message.destroy("opening_mailer_key");
            if (onErrorCB) onErrorCB();
          },
        }),
      );
      return;
    }

    executeStatusUpdate(status, onSuccessCB, onErrorCB);
  };

  // --- Helper: Status Class ---

  // --- Configuration Items (Mirroring CreateQuotations Structure) ---
  const items = useMemo(() => {
    const tabs = [
      {
        label: `Customer Details`,
        key: CreateQuotationStepsEnum.CUSTOMER_DETAILS,
        component: <CustomerDetails data={data} setData={setData} />,
      },
      {
        label: `Bespoke / Bought-In Items`,
        key: CreateQuotationStepsEnum.BESPOKE_ITEMS,
        component: <BespokeItemsDetails data={data} />,
      },
      {
        label: `Services`,
        key: CreateQuotationStepsEnum.SERVICES,
        component: <ServiceDetails data={data} />,
      },
      /* {
        label: `BOM Details`,
        key: CreateQuotationStepsEnum.BOM_DETAILS,
        component: <BOMDetails data={data} showNavigationButtons={false} />,
      }, */
      // ...((data?.categories_snapshot || []).some(
      //   (item: any) => item?.slug === "adjustable-pallet-racking-apr",
      // )
      //   ? [
      {
        label: `Load Notices and Signs`,
        key: CreateQuotationStepsEnum.LOAD_NOTICES,
        component: <LoadNoticeDetails data={data} quoteId={id ?? ""} />,
      },
      //   ]
      // : []),
      // ...(data?.installation_mark_up_required
      //   ? [
      //       {
      //         label: (
      //           <span>
      //             Installation Checklist
      //             {totalQuestions > 0 && (
      //               <span className="ms-2">
      //                 ({checklistCount} / {totalQuestions})
      //               </span>
      //             )}
      //           </span>
      //         ),
      //         key: CreateQuotationStepsEnum.INSTALLATION_CHECKLIST,
      //         component: <ChecklistDetails data={data} />,
      //       },
      //     ]
      //   : []),
      {
        label: `Delivery`,
        key: CreateQuotationStepsEnum.COLLECTION_DELIVERY,
        component: <DeliveryDetails data={data} />,
      },
      {
        label: `Notes`,
        key: CreateQuotationStepsEnum.NOTES,
        component: <NotesDetails data={data} quoteId={id ?? ""} />,
      },
    ];
    tabs.push({
      label: `Designs`,
      key: CreateQuotationStepsEnum.DESIGNS,
      component: <DesignsDetails data={data} />,
    });
    tabs.push({
      label: `Review`,
      key: CreateQuotationStepsEnum.REVIEW,
      component: <Review readOnly={true} quoteId={id} />,
    });

    return tabs;
  }, [data]);

  const getStepIndex = (step: string) => {
    const idx = fullStepPlaylist.findIndex((item) => item === step);
    return idx !== -1 ? idx : 0;
  };

  const activeComponent = useMemo(() => {
    // 1. Check if it's a dynamic step (e.g. [categoryId]__products)
    if (activeStep.includes("__")) {
      const [categoryId, subStep] = activeStep.split("__");
      const groupData = STATIC_STORAGE_GROUPS.find((g) => g.key === categoryId);

      if (groupData) {
        if (
          subStep === CreateQuotationStepsEnum.SUMMARY ||
          subStep === CreateQuotationStepsEnum.PRODUCTS ||
          subStep === CreateQuotationStepsEnum.ACCESSORIES
        ) {
          return (
            <ProductDetails
              data={data}
              activeTab={subStep}
              categoryData={groupData}
              quoteId={id ?? ""}
            />
          );
        } else if (
          subStep === CreateQuotationStepsEnum.FLOOR_DETAILS ||
          subStep === "floor-details"
        ) {
          return (
            <div className="d-flex flex-column gap-4 animate-fade-in">
              <h3 className="fs-4 fs-4-mobile fw-bold text-black d-flex flex-wrap column-gap-3 row-gap-1 align-items-center">
                <span className="order-sm-1 order-1">
                  {groupData?.product_type_name || "Storage"} Type:{" "}
                </span>
                <span className="text-primary fw-normal d-inline-flex align-items-center gap-2 order-sm-2 order-3">
                  <img
                    src={
                      groupData?.image
                        ? getFullAwsUrl(groupData.image, true)
                        : placeHolderImage
                    }
                    width={32}
                    height={32}
                    className="rounded-2 object-fit-cover"
                    alt="Type"
                  />{" "}
                  {groupData?.label || "-"}
                </span>
              </h3>
              <hr className="m-0" />
              <EstimationFloorDetails readOnly={true} data={data} />
            </div>
          );
        }
      }
    }

    // 2. Check if it's a static item
    const staticItem = items.find((i) => i.key === activeStep);
    if (staticItem) return staticItem.component;

    // 3. Replace fallback
    return items[0]?.component;
  }, [activeStep, items, STATIC_STORAGE_GROUPS, data]);

  const ActionMenu = () => {
    if (isClientViewingOwnQuote) {
      return (
        <Menu>
          {data?.status === QuoteStatus.DRAFT && (
            <>
              {USER_PERMISSIONS.MODIFY && (
                <Menu.Item key="3" onClick={handleModify}>
                  Modify
                </Menu.Item>
              )}
              {USER_PERMISSIONS.REMOVE && (
                <Menu.Item
                  key="4"
                  danger
                  onClick={() => {
                    setShowDeleteModal(true);
                  }}
                >
                  Remove
                </Menu.Item>
              )}
            </>
          )}
          {USER_PERMISSIONS.READ &&
            data?.status !== QuoteStatus.DRAFT &&
            data?.status !== QuoteStatus.EXPIRED && (
              <Menu.Item
                key="quotation_pdf"
                onClick={() => {
                  if (exporting) return;
                  const closeMessage = message.loading(
                    "Generating PDF, please wait...",
                    0,
                  );
                  messageCloserRef.current = closeMessage;
                  setExporting(true);
                  dispatch(
                    generateQuotationPdfAPI({
                      payload: { quote_id: data._id },
                      onSuccess: () => {
                        closeMessage();
                        setExporting(false);
                        messageCloserRef.current = null;
                      },
                      onError: () => {
                        closeMessage();
                        setExporting(false);
                        messageCloserRef.current = null;
                      },
                    }),
                  );
                }}
              >
                Quotation PDF
              </Menu.Item>
            )}
        </Menu>
      );
    }

    if (isClientViewingOtherUserQuote) {
      return (
        <Menu>
          {USER_PERMISSIONS.READ && data?.status !== QuoteStatus.EXPIRED && (
            <Menu.Item
              key="quotation_pdf"
              onClick={() => {
                if (exporting) return;
                const closeMessage = message.loading(
                  "Generating PDF, please wait...",
                  0,
                );
                messageCloserRef.current = closeMessage;
                setExporting(true);
                dispatch(
                  generateQuotationPdfAPI({
                    payload: { quote_id: data._id },
                    onSuccess: () => {
                      closeMessage();
                      setExporting(false);
                      messageCloserRef.current = null;
                    },
                    onError: () => {
                      closeMessage();
                      setExporting(false);
                      messageCloserRef.current = null;
                    },
                  }),
                );
              }}
            >
              Quotation PDF
            </Menu.Item>
          )}
        </Menu>
      );
    }

    return (
      <Menu>
        {/* ✅ Add Guard (Create Order) */}
        {USER_PERMISSIONS.ADD &&
          [
            QuoteStatus.SAVED,
            QuoteStatus.SENT,
            QuoteStatus.ADMIN_APPROVED,
            QuoteStatus.APPROVED,
            QuoteStatus.APPROVED_OVERRIDE,
          ].includes(data?.status) && (
            <Menu.Item
              key="create-order"
              onClick={() => {
                setIsCreateOrder(true);
              }}
            >
              Create Order
            </Menu.Item>
          )}
        {/* {USER_PERMISSIONS.MODIFY &&
          [QuoteStatus.EXPIRED].includes(data?.status) && (
            <Menu.Item
              key="re-open"
              onClick={() => {
                setIsReopenModalOpen(true);
              }}
            >
              Re-Open
            </Menu.Item>
          )} */}
        {USER_PERMISSIONS.MODIFY &&
          (isAdminSubAdminLoggedIn() || !data?.admin_approval_required
            ? data?.is_design === false ||
              ![QuoteStatus.DRAFT, QuoteStatus.DESIGN_PENDING].includes(
                data?.status,
              )
            : (data?.is_design === false ||
                ![QuoteStatus.DRAFT, QuoteStatus.DESIGN_PENDING].includes(
                  data?.status,
                )) &&
              ![
                QuoteStatus.DRAFT,
                QuoteStatus.DESIGN_APPROVED,
                QuoteStatus.PENDING_ADMIN_APPROVAL,
              ].includes(data?.status)) &&
          data?.status !== QuoteStatus.ORDER_CREATED &&
          ![
            QuoteStatus.APPROVED,
            QuoteStatus.APPROVED_OVERRIDE,
            QuoteStatus.EXPIRED,
          ].includes(data?.status) && (
            <>
              {/* <Menu.Item
                key="save"
                onClick={() => {
                  changeStatusHandler("saved");
                }}
              >
                Save
              </Menu.Item> */}
              <Menu.Item
                key="save&send"
                onClick={() => {
                  changeStatusHandler("sent");
                }}
              >
                Email & Mark Sent
              </Menu.Item>
            </>
          )}

        {/* {[QuoteStatus.DESIGN_PENDING].includes(data?.status) &&
          [
            RoleTypesEnum.ADMIN,
            RoleTypesEnum.SUB_ADMIN,
            RoleTypesEnum.DESIGN_TEAM,
          ].includes(getUserRole() as RoleTypesEnum) &&
          data?.is_design && (
            <Menu.Item
              key="mark_design_approved"
              onClick={() => {
                changeStatusHandler("design_approved");
              }}
            >
              Mark Design Approved
            </Menu.Item>
          )} */}

        {!isCustomerLoggedIn() &&
          isAdminSubAdminLoggedIn() &&
          [QuoteStatus.DRAFT, QuoteStatus.PENDING_ADMIN_APPROVAL].includes(
            data?.status,
          ) && (
            <Menu.Item
              key="mark_admin_approved"
              onClick={() => {
                if (data?.customer_snapshot?.is_approved === false) {
                  setIsCustomerApprovalRequiredModalOpen(true);
                } else {
                  setIsAdminApprovalModalOpen(true);
                }
              }}
            >
              Mark Admin Approved
            </Menu.Item>
          )}
        {/* {USER_PERMISSIONS.READ &&
          [QuoteStatus.SAVED, QuoteStatus.SENT].includes(data?.status) && (
            <Menu.Item
              key="share_approval_link"
              onClick={async () => {
                // 1. Get the base (http://localhost:3000/sm-crm)
                // window.location.origin handles the protocol and port automatically
                const baseUrl = alias
                  ? `${window.location.origin}/${alias}`
                  : window.location.origin;

                // 2. Get the dynamic ID (assuming 'id' is the property in your data object)

                if (!id) {
                  console.error("No quotation ID found in data.");
                  return;
                }

                // 3. Construct the full URL
                let fullUrl = `${baseUrl}/quotation-details/${id}`;

                if (Capacitor.isNativePlatform()) {
                  fullUrl = getCapacitorBaseUrl(fullUrl);
                }

                try {
                  // 4. Copy to clipboard
                  await navigator.clipboard.writeText(fullUrl);
                  message.success("Approval link copied to clipboard!");
                  // Optional: Add your UI notification here (e.g., message.success)
                  console.log("Copied:", fullUrl);
                } catch (err) {
                  console.error("Failed to copy URL:", err);
                }
              }}
            >
              Share Approval Link
            </Menu.Item>
          )} */}

        {/* ✅ Modify Guard (Override) */}
        {/* {!isCustomerLoggedIn() &&
          USER_PERMISSIONS.MODIFY &&
          [QuoteStatus.SAVED, QuoteStatus.SENT].includes(data?.status) && (
            <Menu.Item
              key="approval_override"
              onClick={() => setIsOverrideModalOpen(true)}
              style={{ color: "#52c41a" }}
            >
              Approval Override
            </Menu.Item>
          )} */}

        {/* ✅ Modify Guard (Notify) */}
        {!isCustomerLoggedIn() &&
          USER_PERMISSIONS.MODIFY &&
          [QuoteStatus.SAVED, QuoteStatus.SENT].includes(data?.status) && (
            <Menu.Item
              key="notify_cleint"
              onClick={() => {
                const targetId = data?._id || id;
                if (targetId) {
                  setNotifyingClient(true);
                  message.loading({
                    content: "Preparing email format and opening mail app...",
                    key: "opening_mailer_key",
                    duration: 0,
                  });
                  dispatch(
                    previewQuoteEmailAPI({
                      payload: { quote_id: targetId },
                      onSuccess: async (emailData) => {
                        setNotifyingClient(false);
                        message.destroy("opening_mailer_key");
                        try {
                          if (Capacitor.isNativePlatform()) {
                            message.loading({
                              content:
                                "Preparing quotation PDF and opening mail app...",
                              key: "opening_mailer_key",
                              duration: 0,
                            });
                            try {
                              await openQuoteEmailWithPdf(targetId, emailData);
                              message.destroy("opening_mailer_key");
                              if (data?.status === QuoteStatus.SAVED) {
                                setIsContantLoading(false);
                                setPendingSentModalOpen(true);
                              }
                            } catch (error) {
                              console.error(
                                "Native email composer error:",
                                error,
                              );
                              message.destroy("opening_mailer_key");
                              message.error(
                                "Failed to prepare the quotation email. Please try again.",
                              );
                            }
                            return;
                          }
                          triggerEmailWithDesktopAppCheck(() => {
                            const targetRoom =
                              localStorage.getItem("sm_target_room");
                            console.log(
                              "Emitting email data to global socket for target room:",
                              targetRoom || "all",
                            );
                            socket.emit("mailer:trigger-email", {
                              target_room: targetRoom || undefined,
                              to: emailData.to || "",
                              cc: emailData.cc || "",
                              subject: emailData.mail_subject || "",
                              body: emailData.html_template || "",
                              pdf_url: emailData.pdf_url || "",
                              pdf_download_url: `${process.env.REACT_APP_API_URL}/quotes/generate-pdf?quote_id=${targetId}&download=true`,
                              pdf_name: emailData.pdf_name || "Quotation.pdf",
                            });
                            if (data?.status === QuoteStatus.SAVED) {
                              setIsContantLoading(true);
                              setPendingSentModalOpen(true);

                              if (mailerTimeoutRef.current)
                                clearTimeout(mailerTimeoutRef.current);
                              mailerTimeoutRef.current = setTimeout(() => {
                                console.warn(
                                  "⚠️ No response from Electron Mailer App within timeout.",
                                );
                                setIsContantLoading(false);
                                setPendingSentModalOpen(false);
                                setShowDesktopAppError(true);
                              }, 4000);
                            }
                          });
                        } catch (error) {
                          console.error(
                            "Could not connect to Desktop App. Is it running?",
                            error,
                          );
                          message.destroy("opening_mailer_key");
                          setShowDesktopAppError(true);
                        }
                      },
                      onError: () => {
                        setNotifyingClient(false);
                        message.destroy("opening_mailer_key");
                      },
                    }),
                  );
                }
              }}
            >
              Notify to Client
            </Menu.Item>
          )}

        {/* ✅ Read Guard (PDF) */}
        {USER_PERMISSIONS.READ &&
          [
            QuoteStatus.SAVED,
            QuoteStatus.SENT,
            QuoteStatus.APPROVED,
            QuoteStatus.APPROVED_OVERRIDE,
            QuoteStatus.ORDER_CREATED,
          ].includes(data?.status) && (
            <Menu.Item
              key="quotation_pdf"
              onClick={() => {
                if (exporting) return;
                const closeMessage = message.loading(
                  "Generating PDF, please wait...",
                  0,
                );
                messageCloserRef.current = closeMessage;
                setExporting(true);
                dispatch(
                  generateQuotationPdfAPI({
                    payload: { quote_id: data._id },
                    onSuccess: () => {
                      closeMessage();
                      setExporting(false);
                      messageCloserRef.current = null;
                    },
                    onError: () => {
                      closeMessage();
                      setExporting(false);
                      messageCloserRef.current = null;
                    },
                  }),
                );
              }}
            >
              Quotation PDF
            </Menu.Item>
          )}

        {/* Download & Mark Sent (Admin Approved only) */}
        {USER_PERMISSIONS.MODIFY &&
          data?.status === QuoteStatus.ADMIN_APPROVED && (
            <Menu.Item
              key="download_and_mark_sent"
              onClick={() => {
                setIsDownloadAndMarkSentModalOpen(true);
              }}
            >
              Download & Mark Sent
            </Menu.Item>
          )}

        {/* ✅ Modify Guard (Edit Quote) */}
        {USER_PERMISSIONS.MODIFY &&
          ![
            QuoteStatus.APPROVED,
            QuoteStatus.APPROVED_OVERRIDE,
            QuoteStatus.EXPIRED,
            QuoteStatus.SENT,
            QuoteStatus.ORDER_CREATED,
          ].includes(data?.status) &&
          (data?.status != QuoteStatus?.ADMIN_APPROVED ||
            [ROLE?.ADMIN, ROLE?.SUB_ADMIN]?.includes(userData?.role?.key)) && (
            <Menu.Item key="3" onClick={handleModify}>
              Modify
            </Menu.Item>
          )}

        {USER_PERMISSIONS.MODIFY && (
          <>
            {data?.status != QuoteStatus?.ADMIN_APPROVED ||
            [ROLE?.ADMIN, ROLE?.SUB_ADMIN]?.includes(userData?.role?.key) ? (
              <Menu.Item
                key="duplicate"
                onClick={() => {
                  setIdToDuplicate(data._id);
                }}
              >
                <div className="text-black d-flex align-items-center gap-2 svg-20">
                  Duplicate
                </div>
              </Menu.Item>
            ) : (
              ""
            )}
            {!!data?.project_no && (
              <Menu.Item
                key="create-revision"
                onClick={() => {
                  setIdToCreateRevision(data._id);
                }}
              >
                <div className="text-black d-flex align-items-center gap-2 svg-20">
                  Create Revision
                </div>
              </Menu.Item>
            )}
          </>
        )}

        {!isCustomerLoggedIn() && USER_PERMISSIONS.READ && (
          <Menu.Item
            key="2"
            onClick={() => {
              navigate(`/quotations/${data._id}/audit-log`, {
                state: {
                  backPath: `/quotations/${data._id}`,
                },
              });
            }}
          >
            Audit Log
          </Menu.Item>
        )}

        {/* ✅ Archive / Unarchive Guard */}
        {[ROLE.ADMIN, ROLE.SUB_ADMIN].includes(userData?.role?.key) && (
          <Menu.Item
            key="toggle_archive"
            onClick={() => {
              setArchiveActionData({
                id: data._id,
                type: data?.is_archived ? "unarchive" : "archive",
              });
            }}
          >
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              {data?.is_archived ? "Unarchive" : "Archive"}
            </div>
          </Menu.Item>
        )}

        {/* ✅ Remove Guard (Delete Quote) */}
        {USER_PERMISSIONS.REMOVE &&
          ![
            QuoteStatus.APPROVED,
            QuoteStatus.APPROVED_OVERRIDE,
            QuoteStatus.ORDER_CREATED,
          ].includes(data?.status) && (
            <Menu.Item key="4" danger onClick={() => setShowDeleteModal(true)}>
              Remove
            </Menu.Item>
          )}
      </Menu>
    );
  };

  if (loading && !data) {
    return (
      <div className="h-100 d-flex justify-content-center align-items-center">
        <Spin size="large" tip="Loading details..." />
      </div>
    );
  }

  const quoteIdDisplay = data?.display_quote_id;
  const companyName = data?.customer_id?.company_name || "Unknown Company";
  const status = data?.status || QuoteStatus.DRAFT;
  const statusLabel = QuoteStatusLabels[status as QuoteStatus] || status;

  const isFinalInvoicePending = !!data?.enable_to_create_final_invoice;

  if (
    data?.is_archived &&
    userData?.role?.key !== ROLE.ADMIN &&
    userData?.role?.key !== ROLE.SUB_ADMIN
  ) {
    return <FullScreenLoader />;
  }

  return USER_PERMISSIONS.READ ? (
    <div className="page-content h-100 px-40 py-40">
      {/* --- HEADER --- */}
      <div
        className={`d-flex flex-column gap-md-2 gap-0 ${
          isInvoiceFeatureEnabled &&
          INVOICE_WARNING_VISIBLE_ROLES.includes(userData?.role?.key) &&
          data?.invoice_warnings &&
          data?.invoice_warnings.length > 0
            ? `mb-2`
            : `mb-3`
        }`}
      >
        <div className="d-flex gap-md-3 gap-2 justify-content-between align-items-md-center align-items-start">
          <div className="d-flex align-items-md-center align-items-start gap-md-3 gap-2 w-100">
            <span
              onClick={() => {
                handleNavigateBack();
              }}
              className="svg-24 cursor-pointer"
            >
              {svg?.backArrow}
            </span>
            <div className="d-flex flex-wrap align-items-center w-100 gap-2">
              <div className="d-flex flex-wrap align-items-center gap-2 me-auto">
                <h2 className="fw-bold page-title mb-0 d-flex flex-wrap align-items-sm-center gap-2">
                  Quotation Details #{quoteIdDisplay} | {companyName}
                  {isInvoiceFeatureEnabled &&
                    isFinalInvoicePending &&
                    data?.order_details?._id && (
                      <span
                        onClick={async (e) => {
                          e.stopPropagation();
                          data?.order_details?._id &&
                            navigate(
                              `/invoices/create-invoice/${data?.order_details?._id}/final`,
                            );
                        }}
                        className="cursor-pointer ms-md-2"
                        style={{
                          color: "red",
                          fontWeight: "normal",
                          fontSize: "14px",
                        }}
                      >
                        Final Invoice Pending -{" "}
                        <span style={{ textDecoration: "underline" }}>
                          Create
                        </span>
                      </span>
                    )}
                </h2>
              </div>
              {/* Status Badge */}
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 gap-md-3">
                <div className="d-flex align-items-center">
                  <span
                    className={
                      "d-inline-flex align-items-center fw-semibold fs-6 fs-6-mob svg-10 quotation-status " +
                      statusFindClass(status)
                    }
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {svg.dotIcon}
                    <span className="ms-1 d-inline-block lh-normal">
                      {statusLabel}
                    </span>
                  </span>
                  {data?.approval_note && (
                    <span
                      className="svg-24 cursor-pointer ms-2"
                      onClick={() => setIsViewNoteModalOpen(true)}
                    >
                      {svg?.approvalOverrideNote}
                    </span>
                  )}
                </div>

                {!isCustomerLoggedIn() &&
                  isAdminSubAdminLoggedIn() &&
                  [
                    QuoteStatus.DRAFT,
                    QuoteStatus.PENDING_ADMIN_APPROVAL,
                  ].includes(data?.status) && (
                    <div
                      className="order-md-3 btn btn-primary d-inline-flex align-items-center justify-content-center btn-46 text-nowrap"
                      onClick={() => {
                        if (data?.customer_snapshot?.is_approved === false) {
                          setIsCustomerApprovalRequiredModalOpen(true);
                        } else {
                          setIsAdminApprovalModalOpen(true);
                        }
                      }}
                    >
                      Mark Admin Approved
                    </div>
                  )}

                {/* {isCustomerLoggedIn() &&
                  isCreatedForSelf &&
                  [QuoteStatus.SAVED, QuoteStatus.SENT].includes(
                    data?.status,
                  ) && (
                    <div
                      className="order-md-3 btn btn-primary d-inline-flex align-items-center justify-content-center btn-46 text-nowrap"
                      onClick={() => navigate(`/quotation-details/${id}`)}
                    >
                      Approve
                    </div>
                  )} */}
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-md-3 gap-2">
            {/* Action Menu */}
            <Dropdown
              overlay={ActionMenu}
              trigger={["click"]}
              placement="bottomRight"
              className="order-md-3 order-2"
            >
              <span className="svg-46">{svg?.downloadBtn}</span>
            </Dropdown>
          </div>
        </div>
      </div>

      {isInvoiceFeatureEnabled &&
        INVOICE_WARNING_VISIBLE_ROLES.includes(userData?.role?.key) &&
        data?.invoice_warnings &&
        data?.invoice_warnings.length > 0 && (
          <Alert
            type="warning"
            showIcon
            className="py-1 px-3 m-0 invoice-warning-alert mb-3"
            style={{ width: "max-content", maxWidth: "100%" }}
            message={
              <div className="fs-6 fs-6-mob fw-normal">
                <strong className="invoice-warning-title">
                  Invoice Warning:
                </strong>
                <span className="d-none d-md-inline ms-1 invoice-warning-text">
                  - {data.invoice_warnings.join(", ")}
                </span>
                <div className="d-md-none ms-2 invoice-warning-text">
                  {data.invoice_warnings.map((w: string, i: number) => (
                    <div key={i}>- {w}</div>
                  ))}
                </div>
              </div>
            }
          />
        )}

      {data?.customer_snapshot &&
        data?.customer_snapshot?.is_approved === false && (
          <Alert
            type="warning"
            showIcon
            className="py-1 px-3 m-0 mb-3"
            style={{ width: "max-content", maxWidth: "100%" }}
            message={
              <div className="fs-6 fs-6-mob fw-normal d-flex align-items-center flex-wrap gap-2">
                <span>
                  {isAdminSubAdminLoggedIn()
                    ? "Customer is not approved. Please review and approve the customer."
                    : "Customer is not approved. Please ask an administrator to approve the customer."}
                </span>
                {isAdminSubAdminLoggedIn() ? (
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-underline border-0 fs-6 fw-semibold text-primary"
                    onClick={() => {
                      if (quotationCustomerId) {
                        navigate(
                          id
                            ? `/clients-distributors/${quotationCustomerId}?quote_id=${id}`
                            : `/clients-distributors/${quotationCustomerId}`,
                        );
                      }
                    }}
                  >
                    Review & Approve
                  </button>
                ) : cooldownSeconds > 0 ? (
                  <span className="text-secondary">
                    You will be able to send reminder again in next{" "}
                    {formatCooldownTime(cooldownSeconds)} minutes
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-underline border-0 fs-6 fw-semibold text-primary"
                    disabled={requestingApproval}
                    onClick={handleRequestApproval}
                  >
                    {requestingApproval ? "Informing..." : "Inform to Admin"}
                  </button>
                )}
              </div>
            }
          />
        )}

      {/* --- STEPS UI (Matched Style) --- */}
      <div className="custom-dark-steps-container mb-3">
        <Steps
          progressDot
          current={getStepIndex(activeStep)}
          direction="horizontal"
          responsive={false}
          items={fullStepPlaylist.map(() => ({}))}
        />
      </div>

      {/* --- MAIN LAYOUT (Matched Grid) --- */}
      <div className="row gy-3">
        {/* Sidebar */}
        <div className="col-lg-2">
          <div className="customTabs create-quotation-tabs bg-white border-0 shadow rounded-3">
            {!screens.lg && (
              <button
                type="button"
                className="quotation-mobile-nav-trigger"
                onClick={() => setIsMobileNavOpen((prev) => !prev)}
              >
                <span className="quotation-mobile-nav-trigger-label">
                  {activeStep?.includes("__") ? (
                    <>
                      <span className="quotation-mobile-nav-trigger-meta">
                        {
                          STATIC_STORAGE_GROUPS.find(
                            (g) => g.key === activeStep?.split("__")[0],
                          )?.label
                        }
                      </span>
                      <span className="quotation-mobile-nav-trigger-title">
                        {
                          STATIC_GROUP_CHILDREN.find(
                            (c) => c.key === activeStep?.split("__")[1],
                          )?.label
                        }
                      </span>
                    </>
                  ) : (
                    <span className="quotation-mobile-nav-trigger-title">
                      {items?.find((i) => i?.key === activeStep)?.label}
                    </span>
                  )}
                </span>
                <span
                  className={`quotation-side-nav-group-arrow ${
                    isMobileNavOpen ? "is-expanded" : ""
                  }`}
                >
                  {svg?.selectDownArrow}
                </span>
              </button>
            )}

            <div
              className={`quotation-side-nav ${
                !screens.lg && !isMobileNavOpen ? "d-none" : ""
              }`}
            >
              {/* Top static items e.g., Customer Details */}
              {items.slice(0, 1).map(({ label, key }) => (
                <button
                  key={key}
                  type="button"
                  className={`quotation-side-nav-item ${
                    activeStep === key ? "is-active" : ""
                  }`}
                  onClick={() => handleTabChange(key)}
                >
                  {label}
                </button>
              ))}

              {/* Dynamic Items */}
              {STATIC_STORAGE_GROUPS.map((group: StorageGroup) => {
                const isExpanded = expandedGroups.includes(group.key);

                return (
                  <div key={group.key} className="quotation-side-nav-group">
                    <button
                      type="button"
                      className="quotation-side-nav-group-header"
                      onClick={() => toggleGroup(group.key)}
                    >
                      <div className="quotation-side-nav-group-media">
                        <img
                          src={
                            group?.image
                              ? getFullAwsUrl(group?.image, true)
                              : placeHolderImage
                          }
                          alt={group.label}
                          className="quotation-side-nav-group-image"
                        />
                        <span className="quotation-side-nav-group-title">
                          {group.label}
                        </span>
                      </div>

                      <span
                        className={`quotation-side-nav-group-arrow ${
                          isExpanded ? "is-expanded" : ""
                        }`}
                      >
                        {svg?.selectDownArrow}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="quotation-side-nav-group-children">
                        {getChildrenForCategory(group)
                          .filter((child) => {
                            if (
                              child.key === CreateQuotationStepsEnum.SUMMARY
                            ) {
                              return CategoriesSlugWithSummaries.includes(
                                (group.slug || "") as CategorySlugs,
                              );
                            }
                            return true;
                          })
                          .map((child) => {
                            const childKey = getStaticNestedStepKey(
                              group.key,
                              child.key,
                            );
                            return (
                              <button
                                key={`${group.key}-${child.key}`}
                                type="button"
                                className={`quotation-side-nav-group-child ${
                                  activeStep === childKey ? "is-active" : ""
                                }`}
                                onClick={() => handleTabChange(childKey)}
                              >
                                {child.label}
                              </button>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Bottom static items */}
              {items.slice(1).map(({ label, key }) => (
                <button
                  key={key}
                  type="button"
                  className={`quotation-side-nav-item ${
                    activeStep === key ? "is-active" : ""
                  }`}
                  onClick={() => handleTabChange(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="col-lg-10 mobile-font-s">
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : (
            activeComponent
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <CommonConfirmationModal
        open={showDeleteModal}
        title="Remove Quotation, Are You Sure?"
        subTitle="Removing this quotation will delete it permanently from the system. This action cannot be undone. Please confirm before proceeding."
        loading={deleting}
        onSuccess={handleDeleteConfirm}
        onClose={() => setShowDeleteModal(false)}
        confirmButtonText="REMOVE"
      />

      <AddApprovalOverrideNoteModal
        open={isOverrideModalOpen}
        onClose={() => setIsOverrideModalOpen(false)}
        quote_id={id as string}
        refreshData={(data) => {
          setData(data);
        }}
      />

      <ViewOverrideNoteModal
        open={isViewNoteModalOpen}
        onClose={() => setIsViewNoteModalOpen(false)}
        note={data?.approval_note || "No notes available"}
      />

      <CommonConfirmationModal
        open={isNotifyClientModalOpen}
        onClose={() => setIsNotifyClientModalOpen(false)}
        onSuccess={() => {
          setNotifyingClient(true);
          const payload = {
            quote_id: data._id,
          };
          dispatch(
            notifyClientAPI({
              payload,
              onSuccess: () => {
                setNotifyingClient(false);
                setIsNotifyClientModalOpen(false);
              },
              onError: () => {
                setNotifyingClient(false);
              },
            }),
          );
        }}
        title="Notify to Client, Are You Sure?"
        subTitle="This action will send an email to the client with the relevant details. Please confirm before proceeding."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Proceed"
        loading={notifyingClient}
      />

      <CommonConfirmationModal
        open={isReopenModalOpen}
        onClose={() => setIsReopenModalOpen(false)}
        onSuccess={() => {
          setIsReopenLoading(true);
          const payload = {
            quote_id: data._id,
          };
          dispatch(
            reopenQuotationAPI({
              payload,
              onSuccess: (data) => {
                setData(data);
                setIsReopenLoading(false);
                setIsReopenModalOpen(false);
              },
              onError: () => {
                setIsReopenLoading(false);
              },
            }),
          );
        }}
        title="Re-Open Quotation, Are You Sure?"
        subTitle={`Reopening this quotation will change its status to "Draft." Any previously completed approvals may need to be completed again. Are you sure you want to continue?`}
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Proceed"
        loading={isReopenLoading}
      />

      {isCreateOrder && (
        <CreateOrderConfirmationModal
          open={isCreateOrder}
          onClose={() => {
            setIsCreateOrder(false);
            pendingOrderPayloadRef.current = null;
          }}
          onSuccess={() => {
            setSubmittingCreateOrder(true);
            const initialPayload = { quote_id: id };
            pendingOrderPayloadRef.current = initialPayload;
            dispatch(
              createOrderAPI({
                payload: initialPayload,
                onSuccess: (resData) => {
                  setSubmittingCreateOrder(false);
                  pendingOrderPayloadRef.current = null;
                  navigate(`/orders/${resData?._id}`);
                  setIsCreateOrder(false);
                },
                onError: () => {
                  setSubmittingCreateOrder(false);
                },
                onAddressIncompleteError: (errData) => {
                  setIsCreateOrder(false);
                  setFailedAddressData(errData?.data);
                },
                onDrawingRequiredError: (data) => {
                  setIsCreateOrder(false);
                  setDrawingRequiredData(data?.data || {});
                },
                onCustomerOnHoldError: (data: any) => {
                  setIsCreateOrder(false);
                  setCustomerOnHoldModal(true);
                  setCustomerOnHoldMessage(data);
                },
              }),
            );
          }}
          quoteDisplayId={data?.display_quote_id as string}
          submitting={submittingCreateOrder}
        />
      )}

      {customerOnHoldModal && (
        <CustomerOnHoldWarningModal
          visible={customerOnHoldModal}
          onClose={() => setCustomerOnHoldModal(false)}
          customerOnHoldMessage={customerOnHoldMessage}
          isCustomerDetails={true}
        />
      )}

      <CommonConfirmationModal
        open={idToDuplicate != null}
        onClose={() => setIdToDuplicate(null)}
        onSuccess={() => {
          setIsDuplicating(true);
          const payload = {
            quote_id: idToDuplicate,
          };
          dispatch(
            duplicateQuotationAPI({
              payload,
              onSuccess: () => {
                sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
                sessionStorage.removeItem(QUOTATION_FILTER_KEY);
                sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");
                setIsDuplicating(false);
                setIdToDuplicate(null);
                navigate(`/quotations`);
              },
              onError: () => {
                setIsDuplicating(false);
              },
            }),
          );
        }}
        title="Duplicate Quotation, Are You Sure?"
        subTitle="This action will create a copy of the selected quotation with the same details in the Draft stage. You can modify the duplicated quotation as needed."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Duplicate"
        loading={isDuplicating}
      />

      <CommonConfirmationModal
        open={!!idToCreateRevision}
        onClose={() => setIdToCreateRevision(null)}
        onSuccess={() => {
          setIsCreatingRevision(true);
          const payload = {
            quote_id: idToCreateRevision,
          };
          dispatch(
            createRevisionQuotationAPI({
              payload,
              onSuccess: () => {
                setIsCreatingRevision(false);
                setIdToCreateRevision(null);
                navigate("/quotations");
              },
              onError: () => {
                setIsCreatingRevision(false);
              },
            }),
          );
        }}
        title="Create Revision, Are You Sure?"
        subTitle="Are you sure you want to create a new revision of this quotation? Current quotation will be marked as superseded."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Create Revision"
        loading={isCreatingRevision}
      />

      <CommonConfirmationModal
        open={isAdminApprovalModalOpen}
        onClose={() => setIsAdminApprovalModalOpen(false)}
        onSuccess={() => {
          setApproving(true);
          changeStatusHandler(
            "admin_approved",
            () => {
              setApproving(false);
              setIsAdminApprovalModalOpen(false);
            },
            () => {
              setApproving(false);
            },
          );
        }}
        title="Mark This Quotation as Admin Approved?"
        subTitle="Are you sure you want to mark this quotation as Admin Approved? This will update the quotation's approval status and allow it to proceed to the next stage of the workflow."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Approve"
        loading={approving}
      />

      <CommonConfirmationModal
        open={isDownloadAndMarkSentModalOpen}
        onClose={() => {
          if (!isDownloadingAndMarkingSent) {
            setIsDownloadAndMarkSentModalOpen(false);
          }
        }}
        onSuccess={() => {
          if (id) {
            setIsDownloadingAndMarkingSent(true);
            const closeLoadingMsg = message.loading(
              "Generating PDF and updating status...",
              0,
            );

            // Step 1: Generate & download PDF
            dispatch(
              generateQuotationPdfAPI({
                payload: { quote_id: id as string },
                onSuccess: () => {
                  // Step 2: On PDF success, update status to 'sent'
                  executeStatusUpdate(
                    "sent",
                    () => {
                      closeLoadingMsg();
                      setIsDownloadingAndMarkingSent(false);
                      setIsDownloadAndMarkSentModalOpen(false);
                    },
                    () => {
                      closeLoadingMsg();
                      setIsDownloadingAndMarkingSent(false);
                      message.warning(
                        "Quotation PDF downloaded, but failed to update status to Sent. Please try again.",
                      );
                    },
                  );
                },
                onError: () => {
                  closeLoadingMsg();
                  setIsDownloadingAndMarkingSent(false);
                  message.error(
                    "Failed to generate Quotation PDF. Status was not changed.",
                  );
                },
              }),
            );
          }
        }}
        title="Download & Mark as Sent?"
        subTitle="Are you sure you want to download this Quotation PDF & change status as Sent?"
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Proceed"
        loading={isDownloadingAndMarkingSent}
      />

      <CommonConfirmationModal
        open={isCustomerApprovalRequiredModalOpen}
        onClose={() => setIsCustomerApprovalRequiredModalOpen(false)}
        onSuccess={() => {
          setIsCustomerApprovalRequiredModalOpen(false);
          if (quotationCustomerId) {
            navigate(
              id
                ? `/clients-distributors/${quotationCustomerId}?quote_id=${id}`
                : `/clients-distributors/${quotationCustomerId}`,
            );
          }
        }}
        title="Customer Approval Required"
        subTitle="This customer has not been approved yet. Please review and approve the customer details before approving this quotation."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="View Customer Details"
      />

      <CommonConfirmationModal
        open={archiveActionData != null}
        title={
          archiveActionData?.type === "archive"
            ? "Archive Quotation, Are You Sure?"
            : "Unarchive Quotation, Are You Sure?"
        }
        subTitle={
          archiveActionData?.type === "archive"
            ? "Archiving this quotation will move it to the archived list. You can restore it later if needed."
            : "Unarchiving this quotation will restore it to the main list."
        }
        loading={isTogglingArchive}
        onSuccess={handleArchiveConfirm}
        onClose={() => setArchiveActionData(null)}
        confirmButtonText={
          archiveActionData?.type === "archive" ? "ARCHIVE" : "UNARCHIVE"
        }
      />

      <CommonConfirmationModal
        open={pendingSentModalOpen}
        onClose={() => {
          if (mailerTimeoutRef.current) clearTimeout(mailerTimeoutRef.current);
          setPendingSentModalOpen(false);
          setPendingSentCBs(null);
        }}
        onSuccess={() => {
          if (mailerTimeoutRef.current) clearTimeout(mailerTimeoutRef.current);
          setPendingSentModalOpen(false);
          executeStatusUpdate(
            "sent",
            pendingSentCBs?.onSuccessCB,
            pendingSentCBs?.onErrorCB,
          );
          setPendingSentCBs(null);
        }}
        title="Has the Email Been Sent?"
        subTitle="Please confirm once you have sent the email to the client using your default mail app."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Yes, Email Sent"
        isContantLoading={isContantLoading}
      />

      <DownloadMailerAppModal
        open={showDesktopAppError}
        onClose={() => setShowDesktopAppError(false)}
      />

      <DesktopUpdateModal
        visible={showDesktopUpdateModal}
        onClose={() => {
          setShowDesktopUpdateModal(false);
          pendingSendMailRef.current = null;
        }}
        onProceed={() => {
          if (pendingSendMailRef.current) {
            pendingSendMailRef.current();
            pendingSendMailRef.current = null;
          }
        }}
        requiredVersion={process.env.REACT_APP_DESKTOP_APP_VERSION || "1.0.1"}
        currentVersion={desktopAppCurrentVersion}
      />

      {failedAddressData !== null && (
        <ModifyDeliveryAddressModal
          open={failedAddressData !== null}
          onClose={() => {
            setFailedAddressData(null);
            pendingOrderPayloadRef.current = null;
          }}
          addressData={failedAddressData?.customer_snapshot?.delivery_address}
          isDistributor={false}
          onSubmitOverride={(payload, setSaving) => {
            setSaving(true);
            const combinedPayload = {
              ...(pendingOrderPayloadRef.current || {}),
              ...payload,
            };
            pendingOrderPayloadRef.current = combinedPayload;
            dispatch(
              createOrderAPI({
                payload: combinedPayload,
                onSuccess: (data) => {
                  setSaving(false);
                  setFailedAddressData(null);
                  pendingOrderPayloadRef.current = null;
                  navigate(`/orders/${data?._id}`);
                },
                onError: () => {
                  setSaving(false);
                },
                onAddressIncompleteError: (data) => {
                  setFailedAddressData(data?.data);
                },
                onDrawingRequiredError: (data) => {
                  setFailedAddressData(null);
                  setDrawingRequiredData(data?.data || {});
                },
              }),
            );
          }}
          quoteData={failedAddressData}
          currency={failedAddressData?.currency || data?.currency}
          conversion_rate={
            failedAddressData?.conversion_rate || data?.conversion_rate
          }
        />
      )}

      {drawingRequiredData !== null && (
        <ConfigureDrawingsModal
          open={drawingRequiredData !== null}
          onClose={() => {
            setDrawingRequiredData(null);
            pendingOrderPayloadRef.current = null;
          }}
          drawingsData={drawingRequiredData?.variants_requiring_drawing}
          partsData={drawingRequiredData?.parts_requiring_drawing}
          componentsData={drawingRequiredData?.components_requiring_drawing}
          fullDrawingData={drawingRequiredData}
          quotationId={drawingRequiredData?.quote_id}
          onSubmit={(payload, setModalLoader) => {
            setModalLoader(true);
            const combinedPayload = {
              ...(pendingOrderPayloadRef.current || {}),
              ...payload,
            };
            pendingOrderPayloadRef.current = combinedPayload;
            dispatch(
              createOrderAPI({
                payload: combinedPayload,
                onSuccess: (data) => {
                  setModalLoader(false);
                  pendingOrderPayloadRef.current = null;
                  setDrawingRequiredData(null);
                  navigate(`/orders/${data?._id}`);
                },
                onError: () => setModalLoader(false),
                onAddressIncompleteError: (data) => {
                  setModalLoader(false);
                  setDrawingRequiredData(null);
                  setFailedAddressData(data?.data);
                },
                onDrawingRequiredError: (data) => {
                  setIsCreateOrder(false);
                  setDrawingRequiredData(data?.data || {});
                },
              }),
            );
          }}
        />
      )}
    </div>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default QuotationDetails;
